<template>
  <section 
    class="cx-sec m-pop-attraction -border-top -border-bottom"
    ref="container">
    <div class="cx-modal map-loading" v-if="loading">
      <div class="cx-modal__body">
        <div class="cx-card--notify">
          <div class="cx-div">
            <i class="fas fa-circle-notch fa-spin fa-4x"></i>
          </div>
        </div>
      </div>
    </div>   
    <div class="cx-wrap--page m-wrap--page">
      <div class="m-pop-attraction__info">
        <div class="cx-div--outer">
          <div class="cx-div">
            <h2 :aria-label="$t('home.attractionHeading_1')" class="cx-t20 m-pop-attraction__title"
              v-html="$t('home.attractionHeading_1') + '<br>' + $t('home.attractionHeading_2')"
            ></h2>
          </div>
          <div class="cx-div">
            <router-link-localed to="/sightseeing" class="cx-link m-pop-attraction__seeall">
              <span class="cx-link__icon">
                <i class="fa-2x fal fa-camera-retro"></i>
              </span>
              <span>{{$t('home.seeMoreAttraction')}}</span>
            </router-link-localed>
          </div>        
        </div>
        <div class="cx-div--outer m-pop-attraction__list">
          <div v-for="rowNum in attractionRow" :key="rowNum" 
            class="cx-row--halfgutter">
            <div
              v-for="attraction in [0, 1, 2]
                .map(idx => rowNum * 3 - 3 + idx)
                .filter(idx => idx < attractions.length)
                .map(idx => ({
                  idx: idx,
                  cName: attractions[idx].name,
                  category: attractions[idx].category,
                  detail: attractions[idx].attractions[0],
                }))"
              :key="attraction.category"
              @mouseenter="active(attraction.idx)"
              @mouseleave="dismiss(attraction.idx)"
              class="cx-col-sm-4 cx-col-xs-6 m-pop-attraction__card" 
              :class="{ '--active': activeIdx == attraction.idx}"
            >
              <article class="cx-card--wide -border -round-corner">
                <router-link-localed :to="attraction.detail.to">
                  <div class="cx-media">
                    <cx-img class="cx-media__img"
                      tag="div"
                      :imgSrc="getMediaHost(attraction.detail.thumbnail, 'MGTO_MEDIA')"
                      lazyLoad
                    >
                      <div class="cx-media__caption cx-t01 m-subtitle"
                      >{{attraction.cName}}</div>
                    </cx-img>
                    <div class="cx-media__icon">
                      <div class="cx-icon--solid"
                      >{{String.fromCharCode(65 + attraction.idx)}}</div>
                    </div>
                  </div>
                  <h3 class="cx-t20">{{attraction.detail.name}}</h3>
                </router-link-localed>
              </article>
            </div>
          </div>
        </div>

      </div>
    </div>
    <div
      v-if="disableMapbox"
      class="m-pop-attraction__map m-pop-attraction__map-placeholder"
      aria-hidden="true"
    >
      <cx-img
        tag="i"
        class="m-graphic--ngi"
        imgSrc="/static/graphic/mgto_graphic_location.png"
      ></cx-img>
    </div>
    <div v-else id="map" ref="map" class="m-pop-attraction__map"></div>
  </section>
</template>

<script>
import lazyLoadHelper from '@/mixins/lazyLoadHelper';

let mbgl;
if (process.env.VUE_ENV !== 'server') {
  mbgl = require('mapbox-gl');
}

import mapHelper from '@/mixins/mapHelper'
import { mapState } from 'vuex';
import SightMapMapContainer from '@/components/SightMapMapContainer';

export default {
  name: 'home-popular-attraction',
  mixins: [mapHelper, lazyLoadHelper],
  uit() {
    return [
      ['home', [
        'attractionHeading_1',
        'attractionHeading_2',
        'seeMoreAttraction'
      ]],
      ['common', [
        'map'
      ]]
    ]
  },
  data() {
    return {
      map: null,
      loading: true,
      projectedMarkers: [],
      activeIdx: -1,
    }
  },
  components: {
    SightMapMapContainer
  },
  computed: {
    ...mapState({
      isMobile: state => state.ui.isMobile,
      isMobileView: state => state.ui.isMobileView,
      disableMapbox: state => state.ui.disableMapbox
    }),
    mapCenterDesktop() {
      return this.$i18n.locale == 'ar' ? [113.600351, 22.151949] : [113.510351, 22.175949]
    },
    mapCenterMobile() {
      return this.$i18n.locale == 'ar' ? [113.553351, 22.154949] : [113.540351, 22.161949]
    },
    attractions() {
      const attractions = this.$store.getters['attraction/getPopular'](this.$i18n.locale).map(({category, name, attractions}, idx) => { 
        return {
          category,
          name,
          attractions: attractions.map((a, aid) => ({
            ...a,
            to: a.group 
              ? `/${this.$i18n.locale}/${category}/${a.group}/${a.id}`
              : `/${this.$i18n.locale}/${category}/${a.id}`
          }))
        }
      });

      return attractions;
    },
    attractionRow() {
      return (Math.floor(this.attractions.length / 3) + (this.attractions.length % 3 == 0 ? 0 : 1));
    }
  },
  mounted() {
    if (this.disableMapbox) {
      this.loading = false;
      return;
    }
    this.lazyLoad(this.$refs.container, () => {
      this.initMap();
    }, {
      root: null,
      rootMargin: '50%',
      threshold: 0,
      keepAlive: false
    });
  },
  methods: {
    initMap() {
      if (this.disableMapbox) return;
      if (this.map) return this.map;

      mbgl.accessToken = this.mgtoMapAccessToken;

      this.map = new mbgl.Map({
        container: this.$refs.map,
        style: this.mgtoMapStyle,
        center: this.isMobileView ? this.mapCenterMobile : this.mapCenterDesktop,
        bearing: 10,
        zoom: 12,
        scrollZoom: false,
        boxZoom: false,
        dragRotate: false,
        dragPan: false,
        keyboard: false,
        doubleClickZoom: false,
        touchZoomRotate: false,
        trackResize: true,
        localIdeographFontFamily: '"Mark Offc Pro Regular", "Lato Bold", "Mark Offc Pro Medium", "Mark Offc Pro Italic", "Noto Sans", "Arial Unicode MS Regular", sans-serif ',
      });

      // this.map.addControl(
      //   new mbgl.NavigationControl(), 
      //   'top-right'
      // );

      setTimeout(() => { this.loading = false }, 1000);
      this.map.on('style.load', () => {
        this.blackAndWhite && this.switchMapToGrayscale()
      });
      this.map.on('load', () => { 
        this.updateMapData();
        this.map.style.stylesheet.layers.forEach(layer => {
          if (layer.type === 'symbol') {
            this.map.removeLayer(layer.id);
          }
        });
      });

      this.$nextTick(() => {
        const canvas = this.map.getCanvas();
        if (canvas) {
          canvas.setAttribute('aria-label', this.$t('common.map'));
        }
      });

      return this.map;
    },
    updateMapData() {
      const _createMarker = (category, a, cidx, idx) => {
        if (idx > 0) {
          const markerDOM = document.createElement('div');
          markerDOM.className = 'map-marker--point';
          return new mbgl.Marker(markerDOM).setLngLat(a.coordinate);
        }

        const markerDOM = document.createElement('a');
        markerDOM.dataset.id = String.fromCharCode(65 + cidx);
        markerDOM.dataset.lng = a.coordinate[0];
        markerDOM.dataset.lat = a.coordinate[1];
        markerDOM.dataset.active = 0;

        const markerDIV = document.createElement('div');
        markerDIV.className = 'cx-icon';
        markerDIV.style.color = this.colorParser('#d7192e');
        markerDIV.innerHTML = `<span class="cx-marker__info">${markerDOM.dataset.id}</span>`;

        markerDOM.appendChild(markerDIV);
        markerDOM.addEventListener('click', () => {
          markerDOM.dataset.active == 0 
            ? this.active(cidx)
            : this.dismiss(cidx)
        })

        markerDOM.addEventListener('mouseover', () => {
          this.active(cidx);
        })

        return new mbgl.Marker(markerDOM).setLngLat(a.coordinate);
      };

      this.removeAllMarker();
      this.handleWindowResize();
      this.loading = false; 
      this.attractions.forEach(({category, name, attractions}, cidx) => {
        attractions.forEach((a, idx) => {
          const makeMarker = (a) => {
            const marker = _createMarker(category, a, cidx, idx);
              const popupHtml = this.createPOIPopup({
                thumbnail: a.thumbnail,
                title: a.name,
                to: a.to
              });
              let popup = new mbgl.Popup({
                closeButton: false,
                className: 'm-popup',
                offset: -10
              }).setDOMContent(popupHtml);
              if (idx != 0) { // Not the most popular
                marker.setPopup(popup);
                if (!this.isMobileView) {
                  this._bindPopupHoverToMarker(marker, popupHtml);
                }                
              };
              marker.addTo(this.map);
              this.projectedMarkers.push(marker);
          }
          if (a.coordinate && Array.isArray(a.coordinate) && a.coordinate.length > 1) {
            if (Array.isArray(a.coordinate[0])) {
              a.coordinate.forEach(coordinate => makeMarker({...a, coordinate}));
            } else {
              makeMarker(a);
            }
          } 
        })
      });

      this.projectedMarkers.forEach(m => {
        const dom = m.getElement();
        if (dom.dataset.id) {
          m.remove().addTo(this.map);
        }
      })
    },
    handleWindowResize() {
      this.map.resize();
    },
    removeAllMarker() {
      while (this.projectedMarkers.length > 0) {
        const m = this.projectedMarkers.pop();
        m.remove();
      }
    },
    active(idx) {
      this.activeIdx = idx;
      this.actvieMarker(String.fromCharCode(65 + idx));
    },
    dismiss(idx) {
      this.activeIdx = -1;
      this.dismissMarker(String.fromCharCode(65 + idx));
    },
    actvieMarker(id) {
      let markers = this.projectedMarkers.filter(m => {
        const dom = m.getElement();
        return dom.dataset.id == id;
      });

      markers.forEach((marker) => {
        const markerDOM = marker.getElement();
        const popup = marker.getPopup();
        if (markerDOM.dataset.active == 1) return ;
        let { lng, lat, id } = markerDOM.dataset;
        markerDOM.dataset.active = 1;
        markerDOM.firstChild.className = 'cx-marker';
        markerDOM.firstChild.style.color = '';
        markerDOM.firstChild.style.backgroundColor = this.colorParser('#d7192e');
        marker.remove();
        marker = new mbgl.Marker(markerDOM, { anchor: 'bottom' })
          .setLngLat([lng, lat])
          .setPopup(popup)
          .addTo(this.map)
      });

      this.projectedMarkers.filter(m => {
        const dom = m.getElement();
        return dom.dataset.id != id && dom.dataset.active == 1;
      }).forEach(m => {
        const dom = m.getElement();
        this.dismissMarker(dom.dataset.id);
      })
    },
    dismissMarker(id) {
      let markers = this.projectedMarkers.filter(m => {
        const dom = m.getElement();
        return dom.dataset.id == id;
      });

      markers.forEach((marker) => {
        const markerDOM = marker.getElement();
        const popup = marker.getPopup();
        if (markerDOM.dataset.active == 0) return ;
        let { lng, lat, id } = markerDOM.dataset;
        markerDOM.dataset.active = 0;
        markerDOM.firstChild.className = 'cx-icon';
        markerDOM.firstChild.style.color = this.colorParser('#d7192e');
        markerDOM.firstChild.style.backgroundColor = '';
        marker.remove();
        marker = new mbgl.Marker(markerDOM)
          .setLngLat([lng, lat])
          .setPopup(popup)
          .addTo(this.map)
      })
    },
  },
  watch: {
    attractions() {
      if (this.map) {
        this.loading = true; 
        this.updateMapData();
        setTimeout(this.handleWindowResize, 0);
      } 
    },
    isMobileView(isMobileView) {
      this.map && this.map.flyTo({center: this.isMobileView ? this.mapCenterMobile : this.mapCenterDesktop});
    }
  }
}
</script>

<style lang="scss">
.m-pop-attraction{
  position: relative;
  overflow: hidden;
  @media (max-width: $breakpoint-md){
    padding-bottom: 1rem;
    padding-top: 10rem;
  }
  @media (max-width: $breakpoint-lg){
    padding-bottom: 2rem;
    padding-top: 15rem;
  }  
  a{
    pointer-events: all;
  }
  .cx-div{
    // In order to show on map
    // @Hugo, @Wallce - need to check
    &>h2, &>a {
      position: relative;
    }
    &>h2{
      pointer-events: none;
    }
  }
  &__list{
    display: flex;
    flex-wrap: nowrap; 
    overflow: auto;
    pointer-events: all;
    @media (max-width: $breakpoint-lg){
      margin-left: -$space-lg;
      margin-right: -$space-lg;   
      padding-left: $space-lg;
      padding-right: $space-lg;
      margin-bottom: 0 !important;
    }
    @media (min-width: $breakpoint-lg){
      pointer-events: none;
      margin: 0;
      overflow: visible;
      // max-width: 50%;
      flex-direction: column;
    }
    @media (min-width: $breakpoint-xl){
      // max-width: 40%;
    }
    .cx-row--halfgutter{
      display: flex;
      flex-wrap: nowrap;
      padding-top: $space-base;      
      @media (max-width: $breakpoint-lg){
        padding-right: $space-base;
      }
    }
  }
  &__title{
    @extend %style-title;
  }
  &__info{
    pointer-events: none;
    position: relative;
    z-index: 2;
    @media (min-width: $breakpoint-lg){
      max-width: 50%;
    }
    @media (min-width: $breakpoint-xl){
      max-width: 40%;
    }    
  }
  &__seeall{
    @extend %style-text-link;
  }
  &__map{
    position: absolute;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 100%;
    &::before{
      @include pos-abs-full;
      content: "";
      background-image: linear-gradient(90deg, rgba($color-white, .7) 0, rgba($color-white, .7) 25%, rgba($color-white, 0) 50%);
      z-index: 1;
      pointer-events: none;
    }
    .mapboxgl-canvas-container.mapboxgl-interactive{
      cursor: auto;
    }
    .map-marker--point {
      cursor: pointer;
    }
    .mapboxgl-marker {
      cursor: pointer;

      &:hover {
        z-index: 1;
      }
    }
  }
  &__map-placeholder{
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgb(238, 238, 238);
  }
  &__card{
    @include normalize-link($color-black);
    @include set-font(.7);
    min-width: 150px;
    @media (max-width: $breakpoint-lg){
      width:170px;
    }
    @extend %transition--transform;
    &:focus-within {
      outline: rgb(37,77,235) auto 1px;
      outline-offset: 1px;
    }
    &:focus-within,
    &.--active{
      transform: translateY(-$space-sm);
      .cx-card--wide{
        box-shadow: 0 0 3px 0px $color-grey;
      }
    }
    .cx-media{
      border: $space-sm solid $color-white;
      &__icon{
        margin-right: 0 !important;
        margin-left: 0 !important;
        .cx-icon--solid{
          border-top-left-radius: 0;
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
          border-bottom-left-radius: 0;
          background-color: $color-red;
        }
      }
      &__caption{
        padding: $space-sm;
      }
    }
    .cx-t20{
      padding: 0 $space-sm $space-sm;
      line-height: 1.2;
      min-height: 80px;
    }
  }
}
</style>
